import { useDeferredValue, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/hooks/useCurrency';
import { PackageSearch, ScanLine, Search } from 'lucide-react';
import type { ProductSummary, ProductType, ProductVariantSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { apiErrorMessage } from '@/services/apiClient';
import { useBarcodeLookupMutation, useProductsQuery } from '../api/catalog.api';
import { ProductTypeBadge } from './ProductTypeBadge';
import { ProductThumb } from './ProductThumb';

interface ProductSearchProps {
  onSelectProduct: (product: ProductSummary) => void;
  /** Called when a scan resolves to an exact variant. The till will use this path. */
  onSelectVariant?: (variant: ProductVariantSummary, product: ProductSummary) => void;
  autoFocus?: boolean;
  maxResults?: number;
  /** Fires when a scan finds nothing — the till uses it for an audible cue. */
  onScanFailure?: () => void;
  /** Limits the text search to one product type. A scan still resolves any barcode; the host decides what it accepts. */
  productType?: ProductType;
}

/**
 * Find-or-scan product lookup.
 *
 * Written standalone so the POS "New Sale" screen can embed it without rewriting the journey:
 * scan a barcode for an exact variant, or type to search by name, SKU or product number. It owns
 * its own queries, so a host supplies only the callbacks.
 */
export function ProductSearch({
  onSelectProduct,
  onSelectVariant,
  autoFocus = false,
  maxResults = 6,
  onScanFailure,
  productType,
}: ProductSearchProps) {
  const { t } = useTranslation();
  const { format: formatMoney } = useCurrency();

  const [term, setTerm] = useState('');
  const [scanCode, setScanCode] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const scanInput = useRef<HTMLInputElement>(null);

  const deferredTerm = useDeferredValue(term);
  const trimmed = deferredTerm.trim();

  const lookup = useBarcodeLookupMutation();
  const { data, isFetching } = useProductsQuery({
    search: trimmed,
    pageSize: maxResults,
    status: 'ACTIVE',
    ...(productType ? { type: productType } : {}),
  });

  // One character matches half the catalogue; wait until the term is selective.
  const isSearching = trimmed.length >= 2;
  const results = isSearching ? (data?.items ?? []) : [];

  /**
   * Puts the cursor back in the scan box.
   *
   * The whole barcode loop depends on this. A scanner is a keyboard, so if focus is anywhere else
   * the next read is typed into nothing — and a cashier scanning a queue of items has no reason to
   * be looking at the screen to notice.
   */
  const refocus = () => scanInput.current?.focus();

  const handleScan = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setScanError(null);

    const code = scanCode.trim();
    if (code.length < 2) {
      refocus();
      return;
    }

    lookup.mutate(code, {
      onSuccess: (result) => {
        setScanCode('');
        // A scan identifies one exact variant, which is more precise than a text match.
        if (onSelectVariant) onSelectVariant(result.variant, result.product);
        else onSelectProduct(result.product);
        refocus();
      },
      onError: (caught) => {
        setScanError(apiErrorMessage(caught) ?? t('catalog.search.barcodeNotFound'));
        // Cleared as well as refocused: leaving the bad code in place means the next scan appends to it, turning one unreadable label into a run of failures.
        setScanCode('');
        if (onScanFailure) onScanFailure();
        refocus();
      },
    });
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleScan} className="flex items-end gap-2">
        <div className="flex-1">
          <Input
            ref={scanInput}
            label={t('catalog.search.scanLabel')}
            dir="ltr"
            autoFocus={autoFocus}
            aria-invalid={scanError ? true : undefined}
            value={scanCode}
            onChange={(event) => setScanCode(event.target.value)}
            placeholder={t('catalog.search.scanPlaceholder')}
          />
        </div>
        <Button type="submit" variant="secondary" isLoading={lookup.isPending}>
          <ScanLine className="size-4" aria-hidden />
          {t('catalog.search.scan')}
        </Button>
      </form>

      {scanError ? <Alert tone="warning">{scanError}</Alert> : null}

      <Input
        type="search"
        label={t('catalog.search.label')}
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        placeholder={t('catalog.search.placeholder')}
      />

      {!isSearching ? (
        <p className="text-muted flex items-center gap-2 px-1 text-xs">
          <Search className="size-3.5 shrink-0" aria-hidden />
          {t('catalog.search.hint')}
        </p>
      ) : isFetching && results.length === 0 ? (
        <div className="border-line rounded-lg border">
          <SkeletonRows rows={2} columns={2} />
        </div>
      ) : results.length === 0 ? (
        <div className="border-line rounded-lg border">
          <EmptyState
            icon={PackageSearch}
            title={t('catalog.search.noResults')}
            description={t('catalog.search.noResultsHint')}
            className="px-4 py-8"
          />
        </div>
      ) : (
        <ul className="divide-line border-line divide-y rounded-lg border">
          {results.map((product) => (
            <li key={product.id}>
              <button
                type="button"
                onClick={() => onSelectProduct(product)}
                className="hover:bg-canvas flex w-full items-center gap-3 px-4 py-3 text-start"
              >
                <ProductThumb image={product.image} alt={product.name} />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-ink truncate text-sm font-medium">{product.name}</span>
                    <ProductTypeBadge type={product.type} />
                  </span>
                  <span className="text-muted mt-0.5 block text-xs">
                    <span className="font-mono">{product.productNumber}</span>
                    {product.brandName ? (
                      <span className="mx-1.5">· {product.brandName}</span>
                    ) : null}
                  </span>
                </span>

                <span className="text-ink shrink-0 text-sm font-medium">
                  {formatMoney(product.fromPriceMinor)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
